<template>
	<v-card-text class="foundation-excavation-list-item">
		<v-card-actions class="py-0">
			<div class="foundation-excavation-name-area">
				<span class="foundation-excavation-name">{{ obj?.name ?? "" }}</span>
			</div>
			<v-menu>
				<template v-slot:activator="{ props }">
					<v-btn variant="text" size="24" icon v-bind="props">
						<v-icon>icon:Overflow-Menu-Vertical</v-icon>
					</v-btn>
				</template>
				<v-list density="compact">
					<v-list-item @click="exportFoundationExcavation">
						<v-icon class="mr-4">icon:Export</v-icon>
						<span class="menu-item-label">{{ $t("EXPORT_AS") }}</span>
					</v-list-item>
					<v-list-item @click="openEdit">
						<v-icon class="mr-4">icon:Pencil</v-icon>
						<span class="menu-item-label">{{ $t("EDIT") }}</span>
					</v-list-item>
					<v-list-item @click="duplicateFoundationExcavation">
						<v-icon class="mr-4">mdi-content-copy</v-icon>
						<span class="menu-item-label">{{ $t("DUPLICATE") }}</span>
					</v-list-item>
					<v-list-item @click="deleteFoundationExcavation">
						<v-icon class="mr-4">icon:Trash-Can</v-icon>
						<span class="menu-item-label">{{ $t("REMOVE") }}</span>
					</v-list-item>
				</v-list>
			</v-menu>
		</v-card-actions>
		<export-foundation-excavation-dialog v-if="obj" v-model="showExportDialog" :obj="obj" />
	</v-card-text>
</template>

<script lang="ts">
import type { FoundationExcavationObject } from "@/models/objects/FoundationExcavationObject";
import { ObjectType } from "@/models/objects/types";
import * as lockUtils from "@/utils/lock-utils";
import { defineComponent } from "vue";
import { mapMutations } from "vuex";
import ExportFoundationExcavationDialog from "./ExportFoundationExcavationDialog.vue";

export default defineComponent({
	name: "FoundationExcavationListItem",
	components: {
		ExportFoundationExcavationDialog,
	},
	props: {
		foundationExcavationId: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			showExportDialog: false,
		};
	},
	computed: {
		obj(): FoundationExcavationObject | undefined {
			return this.$store.state.object.objects.find(
				(o: { type: ObjectType; id: string }) =>
					o.type === ObjectType.FOUNDATION_EXCAVATION &&
					o.id === this.foundationExcavationId,
			);
		},
	},
	methods: {
		...mapMutations(["set_snackbar"]),
		exportFoundationExcavation() {
			if (!this.obj) return;
			this.showExportDialog = true;
		},
		async openEdit() {
			if (!this.obj) return;
			const siteId = this.$route.query.siteId;
			// object/startEdit fails silently on lock conflict, so surface the
			// other-user notice here (same as deleteFoundationExcavation).
			const lockStatus = await lockUtils.checkLockStatus(siteId, this.obj.id);
			if (lockStatus.locked_by) {
				this.set_snackbar({
					text: this.$t("EXCLUSIVE_NOTICE", { lockUser: lockStatus.locked_by }),
					color: "rgba(153, 0, 0, 0.72)",
				});
				return;
			}
			await this.$store.dispatch("object/startEdit", this.obj);
		},
		async duplicateFoundationExcavation() {
			if (!this.obj) return;
			try {
				await this.$store.dispatch("object/duplicateObject", {
					siteId: this.$route.query.siteId,
					obj: this.obj,
				});
				this.set_snackbar({
					text: this.$t("DUPLICATION_SUCCESSFUL"),
					color: "rgba(0, 153, 0, 0.72)",
				});
			} catch (e) {
				console.error(e);
				if (e instanceof Error && e.message.startsWith("LOCKED:")) {
					const lockUser = e.message.replace("LOCKED:", "");
					this.set_snackbar({
						text: this.$t("EXCLUSIVE_NOTICE", { lockUser }),
						color: "rgba(153, 0, 0, 0.72)",
					});
				} else {
					this.set_snackbar({
						text: this.$t("DUPLICATION_FAILED"),
						color: "rgba(153, 0, 0, 0.72)",
					});
				}
			}
		},
		async deleteFoundationExcavation() {
			if (!this.obj) return;
			try {
				await this.$store.dispatch("object/deleteObject", {
					siteId: this.$route.query.siteId,
					obj: this.obj,
				});
			} catch (e) {
				console.error(e);
				if (e instanceof Error && e.message.startsWith("LOCKED:")) {
					const lockUser = e.message.replace("LOCKED:", "");
					this.set_snackbar({
						text: this.$t("EXCLUSIVE_NOTICE", { lockUser }),
						color: "rgba(153, 0, 0, 0.72)",
					});
				} else {
					this.set_snackbar({
						text: this.$t("DELETION_FAILED"),
						color: "rgba(153, 0, 0, 0.72)",
					});
				}
			}
		},
	},
});
</script>

<style scoped>
.foundation-excavation-list-item {
	padding: 0;
	padding-left: 32px;
}

.foundation-excavation-name-area {
	flex: 1;
	min-width: 0;
}

.foundation-excavation-name {
	color: #fff;
}

.menu-item-label {
	font-size: 13px;
}
</style>
